import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { captureUtm, captureTouch } from "@/lib/api";
import { SiteLayout } from "@/components/SiteLayout";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Investment from "@/pages/Investment";
import Sell from "@/pages/Sell";
import Investors from "@/pages/Investors";
import EstateAgents from "@/pages/EstateAgents";
import Contact from "@/pages/Contact";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Opportunities from "@/pages/Opportunities";
import OpportunityDetail from "@/pages/OpportunityDetail";
import Resources from "@/pages/Resources";
import ResourceDetail from "@/pages/ResourceDetail";
import CrmResources from "@/pages/crm/CrmResources";

import AdminLogin from "@/pages/admin/AdminLogin";
import AuthCallback from "@/pages/admin/AuthCallback";
import AdminHome from "@/pages/admin/AdminHome";
import CrmLeads from "@/pages/crm/CrmLeads";
import AdminProjects from "@/pages/admin/AdminProjects";
import AdminOpportunities from "@/pages/admin/AdminOpportunities";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import LeadDetail from "@/pages/admin/LeadDetail";
import CrmDashboard from "@/pages/crm/CrmDashboard";
import CrmToday from "@/pages/crm/CrmToday";
import CrmDuplicates from "@/pages/crm/CrmDuplicates";
import CrmSettings from "@/pages/crm/CrmSettings";
import CrmInvestors from "@/pages/crm/CrmInvestors";
import CrmVendorLeads from "@/pages/crm/CrmVendorLeads";
import CrmVendorLeadDetail from "@/pages/crm/CrmVendorLeadDetail";
import CrmInvestorForm from "@/pages/crm/CrmInvestorForm";
import CrmInvestorDetail from "@/pages/crm/CrmInvestorDetail";
import CrmProperties from "@/pages/crm/CrmProperties";
import CrmPropertyForm from "@/pages/crm/CrmPropertyForm";
import CrmPropertyDetail from "@/pages/crm/CrmPropertyDetail";
import CrmContacts from "@/pages/crm/CrmContacts";
import CrmContactsHub from "@/pages/crm/CrmContactsHub";
import CrmPipeline from "@/pages/crm/CrmPipeline";
import CrmConversions from "@/pages/crm/CrmConversions";
import CrmContactRecord from "@/pages/crm/CrmContactRecord";
import CrmTasks from "@/pages/crm/CrmTasks";
import CrmReferrals from "@/pages/crm/CrmReferrals";
import CrmReports from "@/pages/crm/CrmReports";
import CrmPlaceholder from "@/pages/crm/CrmPlaceholder";
import CrmArchive from "@/pages/crm/CrmArchive";
import CrmReferralDashboard from "@/pages/crm/CrmReferralDashboard";
import CrmStageReview from "@/pages/crm/CrmStageReview";
import AdminDenied from "@/pages/admin/AdminDenied";
import ProtectedRoute from "@/pages/admin/ProtectedRoute";

const site = (el) => <SiteLayout>{el}</SiteLayout>;

function AppRoutes() {
  const location = useLocation();
  // Process OAuth callback FIRST (synchronously during render) to avoid race conditions
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={site(<Home />)} />
      <Route path="/about" element={site(<About />)} />
      <Route path="/projects" element={site(<Projects />)} />
      <Route path="/projects/:slug" element={site(<ProjectDetail />)} />
      <Route path="/investment" element={site(<Investment />)} />
      <Route path="/sell" element={site(<Sell />)} />
      <Route path="/investors" element={site(<Investors />)} />
      <Route path="/estate-agents" element={site(<EstateAgents />)} />
      <Route path="/contact" element={site(<Contact />)} />
      <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
      <Route path="/privacy-policy" element={site(<PrivacyPolicy />)} />
      <Route path="/opportunities" element={site(<Opportunities />)} />
      <Route path="/opportunities/:slug" element={site(<OpportunityDetail />)} />
      <Route path="/resources" element={site(<Resources />)} />
      <Route path="/resources/:slug" element={site(<ResourceDetail />)} />

      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/denied" element={<AdminDenied />} />
      <Route path="/admin" element={<ProtectedRoute><AdminHome /></ProtectedRoute>} />
      <Route path="/admin/projects" element={<ProtectedRoute><AdminProjects /></ProtectedRoute>} />
      <Route path="/admin/opportunities" element={<ProtectedRoute><AdminOpportunities /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/admin/leads/:id" element={<Navigate to="/crm/leads" replace />} />
      <Route path="/crm" element={<ProtectedRoute><CrmToday /></ProtectedRoute>} />
      <Route path="/crm/overview" element={<ProtectedRoute><CrmDashboard /></ProtectedRoute>} />
      <Route path="/crm/investors" element={<ProtectedRoute><CrmInvestors /></ProtectedRoute>} />
      <Route path="/crm/investors/new" element={<ProtectedRoute><CrmInvestorForm /></ProtectedRoute>} />
      <Route path="/crm/investors/:id" element={<ProtectedRoute><CrmInvestorDetail /></ProtectedRoute>} />
      <Route path="/crm/investors/:id/edit" element={<ProtectedRoute><CrmInvestorForm /></ProtectedRoute>} />
      <Route path="/crm/vendor-leads" element={<ProtectedRoute><CrmVendorLeads /></ProtectedRoute>} />
      <Route path="/crm/resources" element={<ProtectedRoute><CrmResources /></ProtectedRoute>} />
      <Route path="/crm/vendor-leads/:id" element={<ProtectedRoute><CrmVendorLeadDetail /></ProtectedRoute>} />
      <Route path="/crm/properties" element={<ProtectedRoute><CrmProperties /></ProtectedRoute>} />
      <Route path="/crm/properties/new" element={<ProtectedRoute><CrmPropertyForm /></ProtectedRoute>} />
      <Route path="/crm/properties/:id" element={<ProtectedRoute><CrmPropertyDetail /></ProtectedRoute>} />
      <Route path="/crm/properties/:id/edit" element={<ProtectedRoute><CrmPropertyForm /></ProtectedRoute>} />
      <Route path="/crm/contacts" element={<ProtectedRoute><CrmContactsHub /></ProtectedRoute>} />
      <Route path="/crm/contact/:store/:id" element={<ProtectedRoute><CrmContactRecord /></ProtectedRoute>} />
      <Route path="/crm/pipeline" element={<ProtectedRoute><CrmPipeline /></ProtectedRoute>} />
      <Route path="/crm/conversions" element={<ProtectedRoute><CrmConversions /></ProtectedRoute>} />
      <Route path="/crm/contacts/:id" element={<ProtectedRoute><CrmContacts /></ProtectedRoute>} />
      <Route path="/crm/leads" element={<ProtectedRoute><CrmLeads /></ProtectedRoute>} />
      <Route path="/crm/leads/:id" element={<ProtectedRoute><LeadDetail /></ProtectedRoute>} />
      <Route path="/crm/tasks" element={<ProtectedRoute><CrmTasks /></ProtectedRoute>} />
      <Route path="/crm/referrals" element={<ProtectedRoute><CrmReferrals /></ProtectedRoute>} />
      <Route path="/crm/reports" element={<ProtectedRoute><CrmReports /></ProtectedRoute>} />
      <Route path="/crm/archive" element={<ProtectedRoute><CrmArchive /></ProtectedRoute>} />
      <Route path="/crm/referral-dashboard" element={<ProtectedRoute><CrmReferralDashboard /></ProtectedRoute>} />
      <Route path="/crm/stage-review" element={<ProtectedRoute><CrmStageReview /></ProtectedRoute>} />
      <Route path="/crm/settings" element={<ProtectedRoute><CrmSettings /></ProtectedRoute>} />
      <Route path="/crm/duplicates" element={<ProtectedRoute><CrmDuplicates /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  useEffect(() => { captureUtm(); captureTouch(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
